/**
 * DragPreviewLine - Live preview line following cursor during drag
 *
 * Features:
 * - Dotted blue line style
 * - Pulsing opacity animation
 * - Smooth bezier curve following cursor
 * - Snapped state when hovering valid drop target
 */

import { motion } from 'framer-motion';
import React from 'react';

import type { Point } from '../types/linking-view.types';
import { calculateBezierPath } from './bezierUtils';

interface DragPreviewLineProps {
  start: Point;
  end: Point;
  isSnapped?: boolean;
}

export const DragPreviewLine: React.FC<DragPreviewLineProps> = ({
  start,
  end,
  isSnapped = false,
}) => {
  const path = calculateBezierPath(start, end);

  // Use green color when snapped to indicate valid connection
  const strokeColor = isSnapped ? '#10b981' : '#3b82f6';

  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Glow effect */}
      <motion.path
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={isSnapped ? 8 : 6}
        strokeOpacity={isSnapped ? 0.25 : 0.15}
        strokeLinecap="round"
      />

      {/* Main line - solid when snapped, dotted when not */}
      <motion.path
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth={2}
        strokeDasharray={isSnapped ? undefined : '4 4'}
        strokeLinecap="round"
        animate={
          isSnapped ? { strokeOpacity: 1 } : { strokeOpacity: [0.6, 1, 0.6] }
        }
        transition={{
          duration: 1,
          repeat: isSnapped ? 0 : Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Start dot */}
      <motion.circle
        cx={start.x}
        cy={start.y}
        r={5}
        fill={strokeColor}
        animate={isSnapped ? { scale: 1 } : { scale: [1, 1.2, 1] }}
        transition={{
          duration: 0.8,
          repeat: isSnapped ? 0 : Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* End dot */}
      <motion.circle
        cx={end.x}
        cy={end.y}
        r={isSnapped ? 6 : 4}
        fill={strokeColor}
        stroke="white"
        strokeWidth={2}
        animate={isSnapped ? { scale: 1 } : { scale: [1, 1.1, 1] }}
        transition={{
          duration: 0.6,
          repeat: isSnapped ? 0 : Infinity,
          ease: 'easeInOut',
        }}
      />
    </motion.g>
  );
};

export default DragPreviewLine;
